import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import api, { ASSET_BASE_URL } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import LikeButton from './LikeButton';
import EmoticonPicker from './EmoticonPicker';
import './LogDetail.css';

interface DuckLog {
  id: number;
  user_id: number;
  ship_name: string;
  location?: string;
  found_date: string;
  image_url?: string;
  notes?: string;
  created_at: string;
  username?: string;
  like_count?: number;
  isLiked?: boolean;
}

interface Comment {
  id: number;
  user_id: number;
  content: string;
  created_at: string;
  username?: string;
}

type ReportState = {
  status: 'idle' | 'loading' | 'success' | 'error';
  message?: string;
};

interface ShipOption {
  id: number;
  name: string;
  description?: string;
}

const LogDetail: React.FC = () => {
  const { logId } = useParams<{ logId: string }>();
  const { user } = useAuth();
  const [log, setLog] = useState<DuckLog | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [commentError, setCommentError] = useState('');
  const [reportStates, setReportStates] = useState<Record<number, ReportState>>({});
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({
    ship_name: '',
    location: '',
    found_date: '',
    notes: '',
  });
  const [editImage, setEditImage] = useState<File | null>(null);
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState('');
  const [ships, setShips] = useState<ShipOption[]>([]);
  const [shipsLoading, setShipsLoading] = useState(false);
  const [shipsError, setShipsError] = useState('');
  const [showEmoticonPicker, setShowEmoticonPicker] = useState(false);

  const fetchLog = useCallback(async () => {
    try {
      const response = await api.get(`/ducks/logs/${logId}`);
      const fetchedLog = response.data.log;
      
      // Like-Status abrufen
      if (user) {
        try {
          const likeResponse = await api.get(`/likes/${logId}/status`);
          fetchedLog.isLiked = likeResponse.data.isLiked;
          fetchedLog.like_count = likeResponse.data.likeCount;
        } catch {
          fetchedLog.isLiked = false;
          fetchedLog.like_count = 0;
        }
      }
      
      setLog(fetchedLog);
      setEditData({
        ship_name: fetchedLog.ship_name || '',
        location: fetchedLog.location || '',
        found_date: fetchedLog.found_date
          ? new Date(fetchedLog.found_date).toISOString().split('T')[0]
          : '',
        notes: fetchedLog.notes || '',
      });
    } catch (error) {
      console.error('Fehler beim Laden des Logs:', error);
    } finally {
      setLoading(false);
    }
  }, [logId, user]);

  const fetchComments = useCallback(async () => {
    try {
      const response = await api.get(`/comments/${logId}`);
      setComments(response.data.comments);
    } catch (error) {
      console.error('Fehler beim Laden der Kommentare:', error);
    }
  }, [logId]);

  useEffect(() => {
    fetchLog();
    fetchComments();
  }, [fetchLog, fetchComments]);

  const loadShips = useCallback(async () => {
    try {
      setShipsLoading(true);
      setShipsError('');
      const response = await api.get('/ships');
      setShips(response.data.ships ?? []);
    } catch (error: any) {
      setShipsError(error.response?.data?.error || 'Schiffe konnten nicht geladen werden.');
    } finally {
      setShipsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadShips();
  }, [loadShips]);

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSubmitting(true);
    setCommentError('');
    try {
      const response = await api.post(`/comments/${logId}`, {
        content: newComment,
      });
      setComments([...comments, response.data.comment]);
      setNewComment('');
      setCommentError('');
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Fehler beim Hinzufügen des Kommentars';
      setCommentError(errorMessage);
      console.error('Fehler beim Hinzufügen des Kommentars:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleReportComment = async (commentId: number) => {
    if (!user) {
      setReportStates((prev) => ({
        ...prev,
        [commentId]: { status: 'error', message: 'Bitte logge dich ein, um zu melden.' },
      }));
      return;
    }

    const categoryInput = window.prompt(
      'Warum meldest du diesen Kommentar?\n' +
        '1 = Beleidigung\n' +
        '2 = Spam\n' +
        '3 = Falscher Inhalt\n' +
        '4 = Sonstiges'
    );
    if (categoryInput === null) {
      return;
    }

    let category: string;
    switch (categoryInput.trim()) {
      case '1':
        category = 'Beleidigung';
        break;
      case '2':
        category = 'Spam';
        break;
      case '3':
        category = 'Falscher Inhalt';
        break;
      default:
        category = 'Sonstiges';
        break;
    }

    const details = window.prompt(
      'Optional: weitere Details zur Meldung eingeben (oder leer lassen).'
    );

    const reason =
      details && details.trim().length > 0
        ? `${category}: ${details.trim()}`
        : category;

    setReportStates((prev) => ({
      ...prev,
      [commentId]: { status: 'loading' },
    }));

    try {
      await api.post(`/comments/${commentId}/report`, { reason });
      setReportStates((prev) => ({
        ...prev,
        [commentId]: { status: 'success', message: 'Meldung gesendet.' },
      }));
    } catch (err: any) {
      const message = err.response?.data?.error || 'Meldung fehlgeschlagen.';
      setReportStates((prev) => ({
        ...prev,
        [commentId]: { status: 'error', message },
      }));
    }
  };

  const handleEditToggle = () => {
    if (!log) return;
    setEditData({
      ship_name: log.ship_name || '',
      location: log.location || '',
      found_date: log.found_date
        ? new Date(log.found_date).toISOString().split('T')[0]
        : '',
      notes: log.notes || '',
    });
    setEditImage(null);
    setUpdateError('');
    setEditing(true);
  };

  const shipSelectOptions = useMemo(() => {
    if (!editData.ship_name) {
      return [...ships].sort((a, b) => a.name.localeCompare(b.name));
    }
    const exists = ships.some((ship) => ship.name === editData.ship_name);
    const base = [...ships];
    if (!exists) {
      base.unshift({
        id: -1,
        name: editData.ship_name,
        description: 'Veralteter Eintrag',
      });
    }
    return base.sort((a, b) => a.name.localeCompare(b.name));
  }, [ships, editData.ship_name]);

  const handleUpdateLog = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!log) return;
    if (!editData.ship_name || !editData.found_date) {
      setUpdateError('Schiffsname und Funddatum sind erforderlich.');
      return;
    }
    try {
      setUpdating(true);
      setUpdateError('');
      const formData = new FormData();
      formData.append('ship_name', editData.ship_name);
      formData.append('location', editData.location);
      formData.append('found_date', editData.found_date);
      formData.append('notes', editData.notes);
      if (editImage) {
        formData.append('image', editImage);
      }
      await api.put(`/ducks/logs/${logId}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await fetchLog();
      setEditing(false);
    } catch (error: any) {
      setUpdateError(error.response?.data?.error || 'Log konnte nicht aktualisiert werden');
    } finally {
      setUpdating(false);
    }
  };

  const handleToggleLike = async () => {
    if (!user || !log) return;
    
    const isCurrentlyLiked = log.isLiked;
    
    try {
      if (isCurrentlyLiked) {
        const response = await api.delete(`/likes/${logId}`);
        setLog((prev) => prev ? { ...prev, isLiked: false, like_count: response.data.likeCount } : null);
      } else {
        const response = await api.post(`/likes/${logId}`);
        setLog((prev) => prev ? { ...prev, isLiked: true, like_count: response.data.likeCount } : null);
      }
    } catch (error) {
      console.error('Fehler beim Liken:', error);
    }
  };

  if (loading) {
    return <div className="loading">Lade Log...</div>;
  }

  if (!log) {
    return <div className="error">Log nicht gefunden</div>;
  }

  return (
    <div className="log-detail">
      <Link to="/dashboard" className="back-link">← Zurück zum Dashboard</Link>
      
      <div className="log-detail-card">
        {log.image_url && (
          <img
            src={`${ASSET_BASE_URL}${log.image_url}`}
            alt="Ente"
            className="log-detail-image"
          />
        )}
        <div className="log-detail-content">
          <h1>🦆 {log.ship_name}</h1>
          {log.location && <p className="location">📍 {log.location}</p>}
          <p className="date">Gefunden am: {new Date(log.found_date).toLocaleDateString('de-DE')}</p>
          {log.notes && <p className="notes">{log.notes}</p>}
          <div className="log-detail-actions">
            {user && (
              <LikeButton
                isLiked={log.isLiked || false}
                likeCount={log.like_count || 0}
                onClick={handleToggleLike}
              />
            )}
            <p className="user">
              Geloggt von:{' '}
              <Link to={`/profile/${log.user_id}`}>{log.username}</Link>
            </p>
          </div>
          {user?.id === log.user_id && !editing && (
            <button className="ghost-button" onClick={handleEditToggle}>
              Fund bearbeiten
            </button>
          )}
        </div>
      </div>

      {editing && (
        <div className="edit-log-card">
          <h2>Fund bearbeiten</h2>
          <form onSubmit={handleUpdateLog} className="edit-log-form">
            <div className="form-group">
              <label htmlFor="ship_name">Schiffsname *</label>
              <div className="ship-select">
                <select
                  id="ship_name"
                  value={editData.ship_name}
                  onChange={(e) => setEditData({ ...editData, ship_name: e.target.value })}
                  disabled={shipsLoading}
                  required
                >
                  <option value="">
                    {shipsLoading ? 'Schiffe werden geladen…' : 'Bitte wählen'}
                  </option>
                  {shipSelectOptions.map((ship) => (
                    <option key={`${ship.id}-${ship.name}`} value={ship.name}>
                      {ship.name}
                    </option>
                  ))}
                </select>
                <button type="button" className="ghost-button" onClick={loadShips} disabled={shipsLoading}>
                  Aktualisieren
                </button>
              </div>
              {shipsError && <p className="field-hint error">{shipsError}</p>}
            </div>
            <div className="form-group">
              <label htmlFor="location">Ort</label>
              <input
                id="location"
                value={editData.location}
                onChange={(e) => setEditData({ ...editData, location: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label htmlFor="found_date">Funddatum *</label>
              <input
                type="date"
                id="found_date"
                value={editData.found_date}
                onChange={(e) => setEditData({ ...editData, found_date: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="notes">Notizen</label>
              <textarea
                id="notes"
                rows={4}
                value={editData.notes}
                onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label htmlFor="image">Neues Bild (optional)</label>
              <input
                type="file"
                id="image"
                accept="image/*"
                onChange={(e) => setEditImage(e.target.files?.[0] || null)}
              />
            </div>
            {updateError && <p className="error-text">{updateError}</p>}
            <div className="edit-actions">
              <button
                type="button"
                className="ghost-button"
                onClick={() => setEditing(false)}
                disabled={updating}
              >
                Abbrechen
              </button>
              <button type="submit" className="button-primary" disabled={updating}>
                {updating ? 'Speichern...' : 'Änderungen speichern'}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="comments-section">
        <h2>Kommentare ({comments.length})</h2>
        
        {user && (
          <form onSubmit={handleSubmitComment} className="comment-form">
            <div className="comment-input-wrapper">
              <textarea
                value={newComment}
                onChange={(e) => {
                  setNewComment(e.target.value);
                  setCommentError(''); // Fehler beim Tippen löschen
                }}
                placeholder="Kommentar schreiben..."
                rows={3}
              />
              <button
                type="button"
                className="emoticon-button"
                onClick={(e) => {
                  e.preventDefault();
                  setShowEmoticonPicker(!showEmoticonPicker);
                }}
                title="Emoticon hinzufügen"
              >
                😊
              </button>
            </div>
            {showEmoticonPicker && (
              <EmoticonPicker
                onSelect={(emoticon) => {
                  setNewComment((prev) => prev + emoticon);
                  setShowEmoticonPicker(false);
                }}
                onClose={() => setShowEmoticonPicker(false)}
              />
            )}
            {commentError && (
              <div className="comment-error" style={{ color: '#ff9ec2', marginBottom: '10px', fontSize: '0.9rem' }}>
                {commentError}
              </div>
            )}
            <button type="submit" disabled={submitting || !newComment.trim()}>
              {submitting ? 'Wird gesendet...' : 'Kommentar hinzufügen'}
            </button>
          </form>
        )}

        <div className="comments-list">
          {comments.length === 0 ? (
            <p>Noch keine Kommentare.</p>
          ) : (
            comments.map((comment) => (
              <div key={comment.id} className="comment">
                <div className="comment-header">
                  <strong>{comment.username}</strong>
                  <span className="comment-date">
                    {new Date(comment.created_at).toLocaleDateString('de-DE')}
                  </span>
                </div>
                <p className="comment-content">{comment.content}</p>
                {user && (
                  <div className="comment-actions">
                    <button
                      type="button"
                      className="ghost-button"
                      onClick={() => handleReportComment(comment.id)}
                      disabled={reportStates[comment.id]?.status === 'loading'}
                    >
                      {reportStates[comment.id]?.status === 'success'
                        ? 'Gemeldet'
                        : reportStates[comment.id]?.status === 'loading'
                          ? 'Wird gesendet...'
                          : 'Kommentar melden'}
                    </button>
                    {reportStates[comment.id]?.message && (
                      <span
                        className={`comment-report-feedback ${
                          reportStates[comment.id]?.status === 'error' ? 'error' : 'success'
                        }`}
                      >
                        {reportStates[comment.id]?.message}
                      </span>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default LogDetail;

